import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const PHOTON_URL = "https://photon.komoot.io/api/";

// Format a Photon feature into a readable label and a value to store.
const formatLabel = (f) => {
  const p = f.properties || {};
  const parts = [];
  if (p.name) parts.push(p.name);
  // Show region/state when relevant
  if (p.state && p.state !== p.name) parts.push(p.state);
  if (p.country && p.country !== p.name) parts.push(p.country);
  return parts.join(", ");
};

const typeBadge = (f) => {
  const t = f.properties?.osm_value || f.properties?.type || "";
  const map = {
    city: "Ville",
    town: "Ville",
    village: "Village",
    country: "Pays",
    state: "Région",
    region: "Région",
    island: "Île",
    hamlet: "Hameau",
    suburb: "Quartier",
  };
  return map[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
};

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

export const DestinationAutocomplete = ({ value, onChange, placeholder }) => {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const skipNextRef = useRef(false);
  const debounced = useDebounced(value, 250);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Fetch suggestions
  useEffect(() => {
    if (skipNextRef.current) { skipNextRef.current = false; return; }
    if (!focused) return;
    const q = (debounced || "").trim();
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    fetch(
      `${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=8&lang=fr&osm_tag=place`,
      { signal: controller.signal }
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const feats = (data.features || []).filter(
          (f) => f.properties?.name && (f.properties?.osm_value || f.properties?.type)
        );
        // Deduplicate by label
        const seen = new Set();
        const uniq = [];
        for (const f of feats) {
          const lbl = formatLabel(f);
          if (!seen.has(lbl)) { seen.add(lbl); uniq.push(f); }
        }
        setResults(uniq);
        setOpen(uniq.length > 0);
        setActiveIdx(-1);
      })
      .catch((err) => { if (err.name !== "AbortError") setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [debounced, focused]);

  const selectItem = (f) => {
    skipNextRef.current = true;
    onChange(formatLabel(f));
    setOpen(false);
    setResults([]);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < results.length) {
        e.preventDefault();
        selectItem(results[activeIdx]);
      } else {
        // free text -> just close
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showIcon = !focused && !value;

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        {showIcon && (
          <MapPin
            className="absolute left-0 top-3 h-4 w-4 text-[#96836B] pointer-events-none transition-opacity"
            aria-hidden
          />
        )}
        <input
          ref={inputRef}
          id="destination"
          data-testid="input-destination"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { setFocused(true); if (results.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          className={cn(
            "luxury-input w-full bg-transparent",
            showIcon ? "pl-7" : "pl-0"
          )}
        />
        {loading && (
          <Loader2 className="absolute right-0 top-3 h-4 w-4 text-[#96836B] animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          data-testid="destination-suggestions"
          role="listbox"
          className="absolute z-30 mt-1 w-full bg-white border border-[#EAE8E3] shadow-[0_12px_30px_-15px_rgba(26,27,24,0.18)] max-h-72 overflow-y-auto"
        >
          {results.map((f, i) => {
            const label = formatLabel(f);
            const badge = typeBadge(f);
            const active = i === activeIdx;
            return (
              <li
                key={`${label}-${i}`}
                role="option"
                aria-selected={active}
                data-testid={`destination-option-${i}`}
                onMouseDown={(e) => { e.preventDefault(); selectItem(f); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={cn(
                  "px-4 py-3 cursor-pointer flex items-center justify-between gap-4 transition-colors border-b border-[#F2EFE9] last:border-b-0",
                  active ? "bg-[#FAF9F6]" : "bg-white hover:bg-[#FAF9F6]"
                )}
              >
                <span className="text-sm text-[#1A1B18]">{label}</span>
                {badge && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#96836B] whitespace-nowrap">
                    {badge}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DestinationAutocomplete;
